import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

const printTypes = ["Free", "Square", "Round"];
const flipOptions = ["No", "Yes"];

const CustomForm = ({ onGenerate }) => {
  // State for all form data and image details
  const [data, setData] = useState({
    data_name: "",
    image_name: "",
    image_data_url: "",
    print_type: "Square", // Default value
    image_flip: "No",
    image_width: "",
    image_height: "",
    top_space: "0",
    left_space: "0",
    column_gap: "0",
    row_gap: "0",
  });

  const [saved, setSaved] = useState(
    JSON.parse(localStorage.getItem("items") || "[]")
  );

  // State to store actual image dimensions for PDF scaling
  const [imageDimensions, setImageDimensions] = useState({
    width: null,
    height: null,
  });

  // Generic handler for all text/select field changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const mmToPx = (mm) => (parseFloat(mm) / 25.4) * 96;

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setData((prevData) => ({
        ...prevData,
        image_name: null,
        image_data_url: null,
      }));
      return;
    }
    setData((prevData) => ({ ...prevData, image_name: file.name }));

    // Use FileReader to get the Data URL for preview and PDF
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;

      setData((prevData) => ({ ...prevData, image_data_url: dataUrl }));

      // Get image dimensions for PDF scaling
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = dataUrl;
    };

    reader.readAsDataURL(file);
  };

  const handleGenerate = () => {
    // Call the parent function with all the collected data
    onGenerate({
      ...data,
      // Pass the actual image dimensions as well
      original_image_width: imageDimensions.width,
      original_image_height: imageDimensions.height,
    });
  };

  const handleSave = () => {
    const savedItems = JSON.parse(localStorage.getItem("items") || "[]");
    localStorage.setItem("items", JSON.stringify([...savedItems, data]));
  };

  return (
    <Grid sx={{ width: "100%" }} container spacing={2} p={1}>
      {data.image_data_url && (
        <Grid
          item
          xs={12}
          display={"flex"}
          justifyContent={"center"}
          width={"100%"}
        >
          <Box
            sx={{
              width: 200,
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              borderRadius: data.print_type === "Round" ? "50%" : "4px",
              background: "#eee",
            }}
          >
            <img
              src={data.image_data_url}
              alt={data.image_name || "Preview"}
              style={{
                width:
                  data.print_type === "Free"
                    ? "100%" // rectangle fits inside
                    : "100%", // square or round still fill container
                height: data.print_type === "Free" ? "auto" : "100%", // Round or Square fill box
                objectFit: data.print_type === "Free" ? "contain" : "cover",
                transform: data.image_flip === "Yes" ? "scaleX(-1)" : "none",
              }}
            />
          </Box>
        </Grid>
      )}
      {/* Image Upload Field */}
      <Grid item size={12}>
        <TextField
          onChange={handleImageUpload}
          type="file"
          label="Pick image"
          fullWidth
          // Note: Value is typically not set for file inputs for security reasons
          InputLabelProps={{ shrink: true }} // Makes label look right for file input
        />
        {data.image_name && (
          <p style={{ marginTop: "4px", fontSize: "0.8rem", color: "grey" }}>
            Selected: **{data.image_name}**
            {imageDimensions.width &&
              ` (${imageDimensions.width}x${imageDimensions.height} px)`}
          </p>
        )}
      </Grid>

      {/* Print Type Select */}
      <Grid item size={6}>
        <TextField
          select
          label="Print Type"
          fullWidth
          name="print_type"
          value={data.print_type}
          onChange={handleChange}
        >
          {printTypes.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Image Flip Select */}
      <Grid item size={6}>
        <TextField
          select
          label="Image Flip"
          fullWidth
          name="image_flip"
          value={data.image_flip}
          onChange={handleChange}
        >
          {flipOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Image Width */}
      <Grid item size={6}>
        <TextField
          fullWidth
          label="Image width"
          name="image_width"
          value={data.image_width}
          onChange={handleChange}
          helperText={"in mm"}
        />
      </Grid>

      {/* Image Height */}
      <Grid item size={6}>
        <TextField
          fullWidth
          label="Image height"
          name="image_height"
          disabled={data.print_type !== "Free"}
          value={
            data.print_type === "Free" ? data.image_height : data.image_width
          }
          onChange={handleChange}
          helperText={"in mm"}
        />
      </Grid>

      {/* Divider */}
      <Grid item size={12}>
        <Divider />
      </Grid>

      {/* Top Space */}
      <Grid item size={6}>
        <TextField
          fullWidth
          label="Top Space"
          helperText={"in mm"}
          name="top_space"
          value={data.top_space}
          onChange={handleChange}
        />
      </Grid>

      {/* Left Space */}
      <Grid item size={6}>
        <TextField
          fullWidth
          label="Left Space"
          helperText={"in mm"}
          name="left_space"
          value={data.left_space}
          onChange={handleChange}
        />
      </Grid>

      {/* Column Gap */}
      <Grid item size={6}>
        <TextField
          fullWidth
          label="Column Gap"
          helperText={"in mm"}
          name="column_gap"
          value={data.column_gap}
          onChange={handleChange}
        />
      </Grid>

      {/* Row Gap */}
      <Grid item size={6}>
        <TextField
          fullWidth
          label="Row Gap"
          helperText={"in mm"}
          name="row_gap"
          value={data.row_gap}
          onChange={handleChange}
        />
      </Grid>

      {/* Generate Button */}
      <Grid item size={12} textAlign={"center"}>
        <Button
          variant="contained"
          onClick={() => handleGenerate(data)}
          disabled={!data.image_data_url}
        >
          Generate PDF
        </Button>
      </Grid>

      <Grid item size={9}>
        <TextField
          fullWidth
          label="Data Name"
          helperText={"in mm"}
          name="data_name"
          value={data.data_name}
          onChange={handleChange}
        />
      </Grid>

      {/* Generate Button */}
      <Grid item size={3} textAlign={"center"}>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Grid>

      <Grid item size={3} textAlign={"center"}>
        {saved.map((i) => (
          <Box>
            <Typography>{i.data_name}</Typography>
          </Box>
        ))}
      </Grid>
    </Grid>
  );
};

export default CustomForm;
